import { ArrowDown, ArrowUp, ChevronsUpDown, Filter } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

export interface FilterWert {
  wert: string;
  label: string;
  anzahl?: number;
}

interface Props {
  titel: string;
  /** Sortierschlüssel dieser Spalte; ohne ihn ist die Spalte nicht sortierbar. */
  sortKey?: string;
  aktiveSortierung?: { key: string; richtung: 'asc' | 'desc' };
  onSortieren?: (key: string) => void;
  /** Auswahl für den Filter; ohne sie hat die Spalte keinen Filter. */
  werte?: FilterWert[];
  gewaehlt?: string[];
  onFiltern?: (werte: string[]) => void;
}

/**
 * Spaltenkopf einer Tabelle: Titel zum Sortieren, Trichter zum Filtern.
 *
 * Die Filterauswahl steht im Kopf und nicht in einer Leiste darüber — bei
 * sieben Spalten sucht man sonst, welcher Filter zu welcher Spalte gehört.
 */
export function SpaltenKopf({
  titel,
  sortKey,
  aktiveSortierung,
  onSortieren,
  werte,
  gewaehlt = [],
  onFiltern,
}: Props) {
  const sortiertNach = sortKey && aktiveSortierung?.key === sortKey;
  const gefiltert = gewaehlt.length > 0;

  const umschalten = (wert: string) => {
    if (!onFiltern) return;
    onFiltern(
      gewaehlt.includes(wert) ? gewaehlt.filter((eintrag) => eintrag !== wert) : [...gewaehlt, wert],
    );
  };

  return (
    <div className="flex items-center gap-1">
      {sortKey && onSortieren ? (
        <button
          type="button"
          onClick={() => onSortieren(sortKey)}
          className="flex items-center gap-1 font-medium hover:text-foreground"
        >
          {titel}
          {sortiertNach ? (
            aktiveSortierung?.richtung === 'asc' ? (
              <ArrowUp className="h-3.5 w-3.5" />
            ) : (
              <ArrowDown className="h-3.5 w-3.5" />
            )
          ) : (
            <ChevronsUpDown className="h-3.5 w-3.5 opacity-30" />
          )}
        </button>
      ) : (
        <span className="font-medium">{titel}</span>
      )}

      {werte && werte.length > 0 && onFiltern && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className={`h-6 w-6 p-0 ${gefiltert ? 'text-primary' : 'opacity-40'}`}
              aria-label={`${titel} filtern`}
            >
              <Filter className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="max-h-80 overflow-y-auto">
            <DropdownMenuLabel>{titel}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {werte.map((eintrag) => (
              <DropdownMenuCheckboxItem
                key={eintrag.wert}
                checked={gewaehlt.includes(eintrag.wert)}
                onCheckedChange={() => umschalten(eintrag.wert)}
                onSelect={(ereignis) => ereignis.preventDefault()}
              >
                {eintrag.label}
                {eintrag.anzahl !== undefined && (
                  <span className="ml-2 text-xs text-muted-foreground">{eintrag.anzahl}</span>
                )}
              </DropdownMenuCheckboxItem>
            ))}
            {gefiltert && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => onFiltern([])}>
                  Filter aufheben
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
